import { useBoolean } from '@aibees/shared/hooks';
import { SxStyle } from '@aibees/shared/theme';
import { classnames } from '@aibees/shared/utils';
import { LoadingButton } from '@mui/lab';
import { Box } from '@mui/material';
import { cloneElement, memo, useCallback, useMemo } from 'react';
import { Icon } from '../icon';
import * as Styled from './styles';
import * as SX from './sx';
import type { CustomDialogProps as Props } from './types';

const slotProps = { backdrop: { sx: SX.backdrop } };

export const CustomDialog = memo((props: Props) => {
  const {
    className,
    state: _state,
    loading,
    headerSx,
    title,
    titleSx,
    subTitle,
    subTitleSx,
    icon,
    iconSx,
    children,
    content: rawContent,
    contentSx,
    actions: _actions,
    actionsSx,
    actionsPrepend,
    closeButton,
    defaultOpen,
    uncontrolledElement,
    onClickUncontrolledElement,
    onOpen,
    onClose,
    disabled,
    hideCloseIconInHeader,
    PaperProps,
    maxWidth = 'xs',
    ...other
  } = props;

  // ✅ Memoized state handler
  const defaultState = useBoolean(
    !!defaultOpen,
    useCallback<(v: boolean) => void>(
      (v) => {
        if (v) onOpen?.();
        else onClose?.();
      },
      [onOpen, onClose]
    )
  );
  const state = useMemo(() => _state || defaultState, [_state, defaultState]);

  // ✅ Memoized click handler
  const onClickElement = useCallback(() => {
    if (!disabled) state.onToggle();
    onClickUncontrolledElement?.();
  }, [disabled, state, onClickUncontrolledElement]);

  // ✅ Use memo for content and actions
  const content = children ?? rawContent;

  const actions = useMemo(() => {
    const base = _actions ? [..._actions] : [];
    if (closeButton) {
      base.push({
        variant: 'outlined',
        ...closeButton,
        children: closeButton?.children || 'Cancel',
        closer: true,
      });
    }
    return base.map((x) => ({
      ...x,
      [x.spinning ? 'loading' : 'disabled']: loading,
      ...(x.disabled ? { disabled: x.disabled } : {}),
    }));
  }, [_actions, closeButton, loading]);

  // ✅ Memoized close handler
  const handleClose = useCallback(() => {
    if (onClose) onClose();
    else state.onFalse();
  }, [onClose, state]);

  // ✅ Memoized keydown handler
  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (event.key === 'Tab') event.stopPropagation();
    },
    []
  );

  return (
    <>
      {uncontrolledElement &&
        cloneElement(uncontrolledElement, {
          ...uncontrolledElement.props,
          onClick: onClickElement,
        })}

      <Styled.Wrapper
        className={classnames('CustomDialog', className)}
        maxWidth={maxWidth}
        open={state.value}
        onClose={handleClose}
        slotProps={slotProps}
        onKeyDown={handleKeyDown}
        PaperProps={PaperProps ?? {}}
        {...other}
      >
        {title && (
          <Styled.Header className="DialogHeader" sx={{ ...headerSx }}>
            <Box sx={{ ...SX.header, ...titleSx } as SxStyle}>
              <Box sx={SX.title}>
                {icon && <Icon name={icon} sxx={iconSx} />}
                <Styled.Title>{title}</Styled.Title>
              </Box>
              {!hideCloseIconInHeader && (
                <Box display="flex">
                  <Icon
                    name="close"
                    size={20}
                    onClick={state.onFalse}
                    sxx={SX.closeIconSx}
                  />
                </Box>
              )}
            </Box>
            {subTitle && (
              <Styled.SubTitle sx={subTitleSx}>{subTitle}</Styled.SubTitle>
            )}
          </Styled.Header>
        )}

        {content && (
          <Styled.Content sx={{ p: 2, ...contentSx }}>{content}</Styled.Content>
        )}

        {(!!actions.length || actionsPrepend) && (
          <Styled.Actions className="CustomDialogActions" sx={actionsSx}>
            {actionsPrepend}
            {actions.map(({ closer, ...btnProps }, key) => (
              <LoadingButton
                {...btnProps}
                key={key}
                onClick={(event: any) => {
                  btnProps.onClick?.({ toggle: state.onFalse, event });
                  if (closer) state.onFalse();
                }}
              />
            ))}
          </Styled.Actions>
        )}
      </Styled.Wrapper>
    </>
  );
});

CustomDialog.displayName = 'CustomDialog';
